import { useCallback, useState } from 'react'

import type { OnlyFansList } from '@api'

import IconClose from '@/assets/icons/icon-close.svg'
import { IconLoading } from '@/assets/icons/icon-loading.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { DialogProps } from '@/dialogs/types'
import { removeUserEditorModel } from '@/models/chattingUI/messageEditor/messageEditor.ts'
import { sendMessageFx } from '@/models/chattingUI/messages/messages.ts'
import { useSendMassMessage } from '@/modules/chatting/hooks/use-mass-message-send.ts'
import { useOpenChat } from '@/modules/chatting/hooks/use-open-chat.ts'
import PeopleIcon from '@/modules/chatting/icons/people.svg'
import type { MessageSendRequest } from '@/modules/chatting/types.ts'
import { ChattingMessageEditor } from '@/modules/chatting/ui/chat-window/chatting-message-editor.tsx'
import { NON_EXISTENT_USER_ID } from '@/modules/chatting/ui/chat-window/constants.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'
import { classNames } from '@/utils/helpers.ts'

import { UsersList } from './UsersList/UsersList.tsx'
import { useMassMessageFans } from './useMassMessageFans.ts'
import { createMassMessageBody } from './utils.ts'

type MobileStep = 'choose-chatter' | 'write-message'

interface Props extends DialogProps {
  defaultIncludedLists?: OnlyFansList['id'][]
}

export const NewMessageDialog = ({
  isOpen,
  onClose,
  defaultIncludedLists,
}: Props) => {
  const { handleOpenChat } = useOpenChat()
  const [mobileStep, setMobileStep] = useState<MobileStep>('choose-chatter')

  const {
    fans,
    isLoading,
    isFetchingNextPage,
    includedLists,
    setIncludedLists,
    excludedLists,
    setExcludedLists,
    includedUsers,
    excludedUsers,
    selectedFans,
    selectedFansCount,
    onFetchNext,
    onToggle,
    onClear,

    query,
    setQuery,
    order,
    setOrder,
    sort,
    setSort,
    filters,
    setFilters,
  } = useMassMessageFans({
    defaultIncludedLists,
  })

  const { sendMassMessage, isSendMassMessageLoading } = useSendMassMessage({
    onSuccess: onClose,
  })

  const handleSendMessage = async (message: Partial<MessageSendRequest>) => {
    if (selectedFansCount === 1) {
      await sendMessageFx({
        userId: selectedFans[0],
        message,
      })
      handleOpenChat(selectedFans[0])
      onClose()
    } else {
      const body = createMassMessageBody({
        message,
        includedUsers,
        includedLists,
        excludedLists,
        excludedUsers,
        total: selectedFansCount,
      })

      return sendMassMessage(body)
    }
  }
  const handleToggleSteps = () => {
    setMobileStep((prev) =>
      prev === 'choose-chatter' ? 'write-message' : 'choose-chatter',
    )
  }

  const resetDialog = useCallback(() => {
    removeUserEditorModel(NON_EXISTENT_USER_ID)
  }, [])

  return (
    <Dialog
      show={isOpen}
      onClose={isSendMassMessageLoading ? undefined : onClose}
      afterLeave={resetDialog}
      bigDialog
      panelClassName="lg:max-w-[1100px] sm:max-w-full max-w-full sm:p-8"
      childrenClassName="max-w-full overflow-hidden"
    >
      <div className="space-y-6">
        <Dialog.Header className="flex flex-row items-center justify-between space-y-0 md:justify-center">
          <Dialog.Title>New Mass Message</Dialog.Title>
          <Button
            onClick={handleToggleSteps}
            disabled={selectedFansCount === 0}
            className="md:hidden"
          >
            {mobileStep === 'choose-chatter' ? 'Next' : 'Previous'}
          </Button>
        </Dialog.Header>
        <div className="flex h-[564px] overflow-hidden rounded-lg border">
          <UsersList
            includedLists={includedLists}
            setIncludedLists={setIncludedLists}
            excludedLists={excludedLists}
            setExcludedLists={setExcludedLists}
            query={query}
            setQuery={setQuery}
            sort={sort}
            setSort={setSort}
            order={order}
            setOrder={setOrder}
            onFetchNext={onFetchNext}
            fans={fans}
            selectedFans={selectedFans}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            toggleChatterSelection={onToggle}
            filters={filters}
            setFilters={setFilters}
            isOpen={isOpen}
            className={classNames({
              'max-md:hidden': mobileStep === 'write-message',
            })}
          />
          <div
            className={classNames(
              'bg-background flex flex-1 flex-col md:max-w-[calc(100%-340px)] max-w-full',
              { 'max-md:hidden': mobileStep === 'choose-chatter' },
            )}
          >
            <div className="bg-card h-[61px] w-full border-b px-6 py-3">
              {isLoading ? (
                <IconLoading className="animate-spin my-2" />
              ) : selectedFans.length === 0 ? (
                <div className="py-1.5 text-secondary-300">
                  Please select fans
                </div>
              ) : (
                <div className="bg-secondary-50 text-secondary-300 dark:bg-secondary-600 flex w-fit max-w-[300px] items-center gap-2 rounded-full px-3 py-1.5">
                  <PeopleIcon />
                  <span>{selectedFansCount} selected</span>
                  <div className="flex-1">
                    <IconButton
                      icon={IconClose}
                      onClick={onClear}
                      className="justify-end"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="relative mb-6 mt-auto px-6">
              <ChattingMessageEditor
                onSendMessage={handleSendMessage}
                canBeSent={selectedFansCount > 0}
                sendAsPriorityAllowed={selectedFansCount > 1}
                isChatMessageEditor={false}
              />
            </div>
          </div>
        </div>
        <Dialog.ClosableFooter
          className="w-full grid-cols-1"
          closeText="Close"
          disabled={isSendMassMessageLoading}
          onClose={onClose}
        />
      </div>
    </Dialog>
  )
}
